// Utility functions for contract interactions with robust retry logic and rate limiting handling

/**
 * Generate a random delay between min and max milliseconds
 * @param min - Minimum delay in milliseconds (default: 100ms)
 * @param max - Maximum delay in milliseconds (default: 500ms)
 * @returns Random delay in milliseconds
 */
const getRandomDelay = (min = 100, max = 500): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Helper function to add a random delay between contract calls
 * @param min - Minimum delay in milliseconds (default: 100ms)
 * @param max - Maximum delay in milliseconds (default: 500ms)
 */
export const randomDelay = (min = 100, max = 500): Promise<void> => {
  const delay = getRandomDelay(min, max);
  return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * Helper function to add a fixed delay between contract calls
 * @param ms - Delay in milliseconds (default: 200ms)
 */
export const delay = (ms = 200): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Robust contract call function that retries until it gets an answer
 * @param contractCall - The contract function to call
 * @param callName - Human-readable name for the call (for logging)
 * @param maxRetries - Maximum number of retry attempts (default: 10)
 * @param baseDelay - Base delay for exponential backoff (default: 1000ms)
 * @returns Promise with the result of the contract call
 */
export const makeContractCall = async (
  contractCall: () => Promise<any>,
  callName: string,
  maxRetries = 10,
  baseDelay = 1000
): Promise<any> => {
  let lastError: any = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Add a small random delay before each attempt to avoid overwhelming the RPC
      if (attempt > 1) {
        await randomDelay(50, 200);
      }

      const result = await contractCall();
      console.log(`✅ ${callName} succeeded on attempt ${attempt}`);
      return result;
    } catch (err: any) {
      lastError = err;
      console.error(`❌ ${callName} failed on attempt ${attempt}:`, err);

      // Check if user rejected the transaction - don't retry
      const isUserRejected =
        err.code === "ACTION_REJECTED" ||
        err.message?.includes("user denied") ||
        err.message?.includes("User denied") ||
        err.message?.includes("MetaMask Tx Signature: User denied") ||
        err.message?.includes("ethers-user-denied");

      if (isUserRejected) {
        throw new Error("Transaction rejected by user");
      }

      // Check if it's a rate limiting error or network error
      const isRateLimited =
        err.code === -32005 ||
        err.message?.includes("rate limit") ||
        err.message?.includes("too many requests") ||
        err.message?.includes("429");

      const isNetworkError =
        err.code === "NETWORK_ERROR" ||
        err.message?.includes("network") ||
        err.message?.includes("timeout") ||
        err.message?.includes("connection");

      // If it's a rate limit or network error, retry with exponential backoff
      if (isRateLimited || isNetworkError) {
        if (attempt < maxRetries) {
          const delay =
            baseDelay * Math.pow(2, attempt - 1) + getRandomDelay(0, 1000);
          console.log(
            `⏳ ${callName} - Retrying in ${Math.round(delay)}ms (attempt ${
              attempt + 1
            }/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
      }

      // For other errors, still retry but with shorter delays
      if (attempt < maxRetries) {
        const delay = Math.min(2000, 500 * attempt) + getRandomDelay(0, 500);
        console.log(
          `⏳ ${callName} - Retrying in ${Math.round(delay)}ms (attempt ${
            attempt + 1
          }/${maxRetries})`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  // If we get here, all retries failed
  const errorMessage =
    lastError?.code === -32005
      ? `Rate limited. Please wait a moment and try again.`
      : `Failed to ${callName} after ${maxRetries} attempts: ${
          lastError?.message || "Unknown error"
        }`;

  throw new Error(errorMessage);
};

/**
 * Helper function to check if a contract exists at an address with retry logic
 * @param provider - Ethers provider instance
 * @param address - Contract address to check
 * @returns Promise<boolean> - True if contract exists, false otherwise
 */
export const checkContractExists = async (
  provider: any,
  address: string
): Promise<boolean> => {
  try {
    const code = await makeContractCall(
      () => provider.getCode(address),
      `check contract existence at ${address}`,
      5 // Fewer retries for existence checks
    );
    return code !== "0x";
  } catch (err) {
    console.error(`Error checking contract existence at ${address}:`, err);
    return false;
  }
};

/**
 * Helper function to make multiple contract calls sequentially with random delays
 * @param calls - Array of contract call functions
 * @param callNames - Array of human-readable names for each call
 * @returns Promise with array of results
 */
export const makeSequentialContractCalls = async (
  calls: (() => Promise<any>)[],
  callNames: string[]
): Promise<any[]> => {
  const results: any[] = [];

  for (let i = 0; i < calls.length; i++) {
    const result = await makeContractCall(calls[i], callNames[i]);
    results.push(result);

    // Add random delay between calls (except for the last one)
    if (i < calls.length - 1) {
      await randomDelay(200, 800);
    }
  }

  return results;
};
